import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMultiStepForm } from "./MultiStepFormContext";
import classes from "./ConsigneeInfo.module.css";
import { useGetConsigneeIds } from "../../Queries";
import { t } from "i18next";

export const ConsigneeInfo: React.FC = () => {
  const navigate = useNavigate();
  const { data } = useGetConsigneeIds();
  const { providerFormData, setFormData } = useMultiStepForm();
  const [searchParams] = useSearchParams();
  const userType = searchParams.get("type");

  // Initialize local state from context on mount
  const [consigneeId, setConsigneeId] = useState(
    providerFormData.consigneeId || ""
  );
  const [noConsignee, setNoConsignee] = useState(
    !!providerFormData.noConsigneeId
  );
  const [acceptedTerms, setAcceptedTerms] = useState(
    providerFormData.dealerAcknowlegement || false
  );
  const [acceptPrivacyNotes, setAcceptPrivacyNotes] = useState(false);
  const [consigneeError, setConsigneeError] = useState("");

  const isNextDisabled = !(
    acceptedTerms &&
    acceptPrivacyNotes &&
    (consigneeId.trim() || noConsignee)
  );

  useEffect(() => {
    if (!consigneeId || noConsignee) {
      setConsigneeError("");
    }
  }, [consigneeId, noConsignee]);

  const handleNext = async () => {
    if (consigneeId && !noConsignee) {
      const isValid = data?.includes(consigneeId.trim());

      if (!isValid) {
        setConsigneeError(t("consignee.validation_error"));
        return;
      }
    }

    setConsigneeError("");
    // Update context data with local state values
    setFormData((prev) => ({
      ...prev,
      consigneeId: consigneeId,
      dealerAcknowlegement: acceptedTerms,
      noConsigneeId: noConsignee,
    }));

    if (!isNextDisabled) {
      if (userType === "provider") {
        navigate("/consent");
      } else if (userType === "user") {
        navigate("/alternate-product");
      }
    }
  };
  const handleBack = () => {
    navigate("/landing");
  };

  return (
    <div className={classes.container}>
      <h3>{t("consignee.page_title")}</h3>
      <div className={classes.formWrapper}>
        <div className={classes.row}>
          <label className={classes.label}>
            {t("consignee.enter_consignee_id")}*
          </label>
          <input
            type="text"
            placeholder={t("consignee.consignee_id")}
            value={consigneeId}
            onChange={(e) => setConsigneeId(e.target.value)}
            disabled={noConsignee}
            className={classes.input}
          />
        </div>
        <p className={classes.helperText}>{t("consignee.helper_text")}</p>
        {consigneeError && (
          <div className={classes.error}>{consigneeError}</div>
        )}

        <div className={classes.row}>
          <label className={classes.label}>
            {t("consignee.no_consignee_id")}*
          </label>
          <div className={classes.checkboxWrapper}>
            <input
              type="checkbox"
              checked={noConsignee}
              onChange={() => setNoConsignee((prev) => !prev)}
              disabled={consigneeId !== ""}
            />
          </div>
        </div>

        <div className={classes.row}>
          <label className={classes.label}>
            {t("consignee.terms_and_conditions")}*
          </label>
          <div className={classes.checkboxWrapper}>
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={() => setAcceptedTerms((prev) => !prev)}
            />
          </div>
        </div>
        <div className={classes.row}>
          <label className={classes.label}>
            {t("consignee.privacy_notice")}{" "}
            <a
              href={
                "https://www.permobil.com/privacy-notice/speedcontroldialrecallpermobilcom"
              }
              target="_blank"
              rel="noreferrer"
            >
              {t("consignee.privacy_notice_text")}
            </a>
            *
          </label>
          <div className={classes.checkboxWrapper}>
            <input
              required
              type="checkbox"
              checked={acceptPrivacyNotes}
              onChange={() => setAcceptPrivacyNotes((prev) => !prev)}
            />
          </div>
        </div>
        <div className={"mandatoryText"}>* {t("common.required")}</div>
        <div className={classes.nextContainer}>
          <button className={classes.backButton} onClick={handleBack}>
            {t("common.back")}
          </button>
          <button
            className={classes.nextButton}
            onClick={handleNext}
            disabled={isNextDisabled}
          >
            {t("common.next")}
          </button>
        </div>
      </div>
    </div>
  );
};
